import {RankedModeTypes} from '../../../../../../../shared/GameEnums'
import {LeaderboardConstants} from '../../../../../../../shared/leaderboard_data/LeaderboardConstants'
import {LeaderboardCategoryStringID} from '../../../../../../../shared/leaderboard_data/LeaderboardEnums'
import {LeaderboardRowData, LeaderboardStickyRowData, LeaderboardTabData} from '../../../../../../../shared/leaderboard_data/LeaderboardInterfaces'
import {RankedSeasonState} from '../../../../../../../shared/ranked/ranked_season_data/RankedSeasonState'
import {TimeUtils} from '../../../../../../../shared/utils/TimeUtils'
import {UIAjaxWrapper} from '../../../../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../../../../shared/scripts/network/UIAPIEndpoints'
import {UIPopupController} from '../../../../../../shared/scripts/ui_popup/UIPopupController'
import {UILeaderboardSidebarTabsController} from '../../../../../../shared/scripts/ui_leaderboard/UILeaderboardSidebarTabsController'
import {UILeaderboardStickyRowController} from '../../../../../../shared/scripts/ui_leaderboard/UILeaderboardStickyRowController'
import {UILeaderboardTableController} from '../../../../../../shared/scripts/ui_leaderboard/UILeaderboardTableController'

import {UIMLeaderboardHorizontalTabsController} from './UIMLeaderboardHorizontalTabsController'
import {UIMLeaderboardSearchController} from './UIMLeaderboardSearchController'
import {TimeoutId} from '@colonist/types'
import {UILeaderboardSeasonProgressController} from '../../../../../../shared/scripts/ui_leaderboard/UILeaderboardSeasonProgressController'
import {navigateToPage} from '../../../../../../shared/scripts/ui_utils/ClientUtils.ts'

export class UIMLeaderboardController {
    private leaderboardUrl: string = LeaderboardConstants.globalLeaderboardUrl
    private rankedModeType: RankedModeTypes = RankedModeTypes.Classic4P
    private readonly activeSeasonData: RankedSeasonState | undefined
    private readonly tabsController: UIMLeaderboardHorizontalTabsController
    private readonly tableController: UILeaderboardTableController
    private readonly stickyRowController: UILeaderboardStickyRowController
    private readonly sidebarTabsController: UILeaderboardSidebarTabsController
    private searchTerm: string
    private isLoadingDataAfterScroll: boolean = false
    private waitForBackend: TimeoutId | undefined

    constructor() {
        UIMLeaderboardController.featureCheck()
        this.stickyRowController = new UILeaderboardStickyRowController()
        this.tableController = new UILeaderboardTableController(() => this.fetchScrollData(), this.stickyRowController)
        this.tabsController = new UIMLeaderboardHorizontalTabsController((leaderboardUrl: string) => this.onHorizontalTabChange(leaderboardUrl))
        this.sidebarTabsController = new UILeaderboardSidebarTabsController((rankedModeType: RankedModeTypes) => this.onRankedModeChange(rankedModeType))
        // // this.ctaController = new UIWLeaderboardCTAController()

        this.initiateParametersFromUrl()
        void this.fetchNewTabs()
        void this.fetchMyStickyRowData()

        new UIMLeaderboardSearchController((searchTerm: string) => {
            this.onSearchChange(searchTerm)
            return undefined
        })

        this.stickyRowController.setSearchValue(this.searchTerm)

        this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, this.searchTerm, false)
    }

    private onSearchChange(searchValue: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, searchValue)
        void this.fetchMyStickyRowData()
        this.stickyRowController.setSearchValue(searchValue)
        this.tableController.updateStickyRowStyle()
    }

    private onRankedModeChange(rankedMode: RankedModeTypes) {
        this.switchRankingCategoryTypeData(rankedMode, this.leaderboardUrl, this.searchTerm)
        void this.fetchMyStickyRowData()
    }

    private onHorizontalTabChange(leaderboardUrl: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, leaderboardUrl, this.searchTerm)
        if(leaderboardUrl == LeaderboardConstants.globalLeaderboardUrl) void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
    }

    private onLeaderboardUrlChange(newLeaderboardUrl: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, newLeaderboardUrl, this.searchTerm)
        void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
    }

    private initiateParametersFromUrl() {
        this.rankedModeType = RankedModeTypes.Classic4P
        const url = new URL(window.location.href)
        const pathSegments = url.pathname.split('/')
        const leaderboardPathSegmentIndex = pathSegments.indexOf('leaderboards')
        const rankedModeType = pathSegments[leaderboardPathSegmentIndex + 1]
        if(RankedModeTypes[rankedModeType] !== undefined) this.rankedModeType = RankedModeTypes[rankedModeType]
        this.leaderboardUrl = LeaderboardConstants.globalLeaderboardUrl
        this.searchTerm = ''
    }

    private switchRankingCategoryTypeData(mode: RankedModeTypes, regionId: string, search: string, push: boolean = true) {
        this.rankedModeType = mode
        this.leaderboardUrl = regionId
        this.searchTerm = search

        this.sidebarTabsController.highlightTab(this.rankedModeType)
        this.tabsController.highlightTab(this.leaderboardUrl)
        this.tableController.clearData()
        this.tableController.scrollToTop()
        this.searchTerm == LeaderboardConstants.emptyUsernameSearch ? void this.fetchScrollData() : void this.fetchSearchData()
    }

    private async fetchScrollData() {
        const isActiveSearchOngoing = this.searchTerm != '' && this.tableController.getRowCount() > 0
        if(isActiveSearchOngoing) return

        if(this.isLoadingDataAfterScroll) return
        this.startWaitingForBackendToRespond()
        const startRank = this.tableController.getRowCount() + 1
        const endRank = startRank + LeaderboardConstants.maximumLeaderboardAPIRankingRangeLimit - 1

        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.data(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined, {
            start: startRank,
            end: endRank,
            search: this.searchTerm,
        })
        this.stopWaitingForBackendToRespond()
        if(data == undefined) return

        if(this.leaderboardUrl == LeaderboardCategoryStringID.Friends && data.json.length == 0) {
            this.tableController.displayNoContentMessage()
            return
        }
        this.tableController.updateRankingData(data.json as LeaderboardRowData[], (newLeaderboardUrl: string) => this.onLeaderboardUrlChange(newLeaderboardUrl))
        this.stickyRowController.tableRowLoadingCompleted()
        this.tableController.updateStickyRowStyle()
    }

    private async fetchSearchData() {
        this.startWaitingForBackendToRespond()
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.usernameSearch(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined, {
            usernameSearchInput: this.searchTerm,
        })
        this.stopWaitingForBackendToRespond()
        if(data == undefined) return

        this.tableController.clearData()
        this.tableController.scrollToTop()
        this.tableController.updateRankingData(data.json as LeaderboardRowData[], (newLeaderboardUrl: string) => this.onLeaderboardUrlChange(newLeaderboardUrl))
        this.stickyRowController.tableRowLoadingCompleted()
        this.tableController.updateStickyRowStyle()
    }

    private startWaitingForBackendToRespond() {
        this.isLoadingDataAfterScroll = true
        const maxTimeToWaitForBackend = TimeUtils.MILLISECONDS
        this.waitForBackend = setTimeout(() => {
            this.stopWaitingForBackendToRespond()
        }, maxTimeToWaitForBackend)
    }

    private stopWaitingForBackendToRespond() {
        this.isLoadingDataAfterScroll = false
        if(this.waitForBackend != undefined) clearTimeout(this.waitForBackend)
    }

    private async fetchNewTabs() {
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.tabs(this.leaderboardUrl), undefined)
        if(response == undefined) return
        const leaderboardTabData = response.json as LeaderboardTabData
        if(leaderboardTabData.activeSeasonData != undefined) leaderboardTabData.activeSeasonData = RankedSeasonState.deserialize(leaderboardTabData.activeSeasonData)
        this.updateTabData(response.json as LeaderboardTabData)
    }

    private updateTabData(availableTabs: LeaderboardTabData) {
        this.tabsController.updateLeaderboardRegionTabs(availableTabs.availableHorizontalTabs, this.leaderboardUrl)
        this.sidebarTabsController.setEnabledRankedModeTypes(availableTabs.activeRankedModeTypes)
        if(availableTabs.activeSeasonData != undefined) new UILeaderboardSeasonProgressController(availableTabs.activeSeasonData)
    }

    private async fetchMyStickyRowData() {
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.stickyRowData(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined)
        if(data == undefined) return

        const myStickyRowData = data.json as LeaderboardStickyRowData | ''
        myStickyRowData == '' ? this.stickyRowController.clearStickyRowData() : this.stickyRowController.updateStickyRowData(myStickyRowData, this.tableController.tableContainer.clientHeight)
    }

    private static featureCheck() {
        const div = document.createElement('div')
        if(div.replaceChildren == undefined) {
            UIPopupController.create({key: 'strings:leaderboardPage.updateBrowser.title'}, {key: 'strings:leaderboardPage.updateBrowser.body'}, () => navigateToPage('/'), () => navigateToPage('/'), false)
            throw new Error('Failed to create leaderboard: Browser does not support required features.')
        }
    }
}
